import React, { useEffect, useState } from "react";
import { Save, Plus, Trash2, Loader2, ImageIcon, ChevronDown } from "lucide-react";
import { adminApi } from "../../lib/api";
import { iconNames } from "../../lib/icons";
import { Button } from "../ui/button";
import { useToast } from "../../hooks/use-toast";
import ImageUpload from "./ImageUpload";
import VideoUpload from "./VideoUpload";

const AnnouncementStats = () => {
  const [stats, setStats] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = () => adminApi.announcementStats.get().then(setStats).catch(() => setStats([]));
  useEffect(() => { load(); }, []);
  const reset = async () => {
    if (!window.confirm("Reset all A/B impression & click counts to zero?")) return;
    setBusy(true);
    try { await adminApi.announcementStats.reset(); await load(); } finally { setBusy(false); }
  };
  if (!stats) return <p className="text-xs text-slate-400 mt-4">Loading results…</p>;
  const byId = Object.fromEntries(stats.map((s) => [s.variant, s]));
  const rows = ["A", "B"].map((id) => byId[id] || { variant: id, impressions: 0, clicks: 0 });
  return (
    <div className="mt-5 rounded-xl border border-slate-100 bg-slate-50 p-4" data-testid="announcement-stats">
      <div className="flex items-center justify-between mb-3">
        <p className="text-xs font-bold uppercase tracking-wide text-slate-500">A/B results</p>
        <button onClick={reset} disabled={busy} className="text-xs font-semibold text-red-500 hover:text-red-600 disabled:opacity-50" data-testid="announcement-stats-reset">Reset counts</button>
      </div>
      <div className="grid grid-cols-4 gap-2 text-xs font-semibold text-slate-400 mb-1 px-1"><span>Variant</span><span className="text-right">Views</span><span className="text-right">Clicks</span><span className="text-right">CTR</span></div>
      {rows.map((r) => {
        const ctr = r.impressions ? ((r.clicks / r.impressions) * 100).toFixed(1) : "0.0";
        return (
          <div key={r.variant} className="grid grid-cols-4 gap-2 text-sm py-1.5 px-1 border-t border-slate-200 items-center" data-testid={`announcement-stat-${r.variant}`}>
            <span className="font-bold text-navy">Variant {r.variant}</span>
            <span className="text-right tabular-nums">{r.impressions}</span>
            <span className="text-right tabular-nums">{r.clicks}</span>
            <span className="text-right tabular-nums font-bold text-teal-700">{ctr}%</span>
          </div>
        );
      })}
    </div>
  );
};

const inputCls = "w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange/40";

const Field = ({ label, value, onChange, area, placeholder }) => (
  <label className="block">
    <span className="text-xs font-semibold text-navy">{label}</span>
    {area
      ? <textarea rows={3} value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} className={`${inputCls} mt-1`} />
      : <input value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} className={`${inputCls} mt-1`} />}
  </label>
);

const IconSelect = ({ value, onChange }) => (
  <label className="block">
    <span className="text-xs font-semibold text-navy">Icon</span>
    <select value={value || ""} onChange={(e) => onChange(e.target.value)} className={`${inputCls} mt-1`}>
      {iconNames.map((n) => <option key={n} value={n}>{n}</option>)}
    </select>
  </label>
);

const Card = ({ title, children, defaultOpen = false }) => {
  const [open, setOpen] = useState(defaultOpen);
  const slug = title.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
  return (
    <div className="bg-white rounded-2xl border border-slate-100 shadow-sm mb-4 overflow-hidden">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        data-testid={`hp-section-toggle-${slug}`}
        className="w-full flex items-center justify-between gap-3 px-6 py-4 text-left hover:bg-slate-50 transition-colors"
      >
        <h2 className="font-display font-bold text-navy">{title}</h2>
        <ChevronDown size={18} className={`text-slate-400 shrink-0 transition-transform duration-200 ${open ? "rotate-180" : ""}`} />
      </button>
      {open && <div className="px-6 pb-6 pt-2 border-t border-slate-100" data-testid={`hp-section-body-${slug}`}>{children}</div>}
    </div>
  );
};

const ListEditor = ({ items, onChange, template, render }) => (
  <div className="space-y-3">
    {(items || []).map((it, i) => (
      <div key={i} className="rounded-xl border border-slate-100 bg-slate-50 p-4 relative">
        <button onClick={() => onChange(items.filter((_, x) => x !== i))} className="absolute top-3 right-3 text-red-400 hover:text-red-600" data-testid={`list-remove-${i}`}><Trash2 size={15} /></button>
        {render(it, (patch) => onChange(items.map((x, xi) => (xi === i ? { ...x, ...patch } : x))))}
      </div>
    ))}
    <Button variant="outline" size="sm" onClick={() => onChange([...(items || []), { ...template }])} className="rounded-full" data-testid="list-add"><Plus size={14} className="mr-1" /> Add</Button>
  </div>
);

export default function HomepageManager() {
  const { toast } = useToast();
  const [form, setForm] = useState(null);
  const [guides, setGuides] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => { adminApi.homepage.get().then(setForm).catch(() => {}); }, []);
  useEffect(() => { adminApi.guides.list().then(setGuides).catch(() => setGuides([])); }, []);
  const set = (key, val) => setForm((f) => ({ ...f, [key]: val }));
  const setHero = (k, v) => setForm((f) => ({ ...f, hero: { ...f.hero, [k]: v } }));
  const setFounder = (k, v) => setForm((f) => ({ ...f, founder: { ...(f.founder || {}), [k]: v } }));
  const setAnn = (k, v) => setForm((f) => ({ ...f, announcement: { ...(f.announcement || {}), [k]: v } }));
  const setVariant = (i, text) => setForm((f) => {
    const variants = [...((f.announcement?.variants) || [])];
    variants[i] = { ...(variants[i] || { id: i === 0 ? "A" : "B" }), text };
    return { ...f, announcement: { ...(f.announcement || {}), variants } };
  });

  const save = async () => {
    setSaving(true);
    try { await adminApi.homepage.save(form); toast({ title: "Homepage saved" }); }
    catch { toast({ title: "Couldn't save", variant: "destructive" }); }
    finally { setSaving(false); }
  };

  if (!form) return <p className="text-sm text-slate-400">Loading…</p>;

  return (
    <div data-testid="homepage-manager">
      <div className="flex items-start justify-between gap-4 mb-6 flex-wrap">
        <div>
          <h1 className="font-display font-extrabold text-2xl text-navy">Homepage</h1>
          <p className="text-sm text-slate-500">Edit the content shown on the public homepage.</p>
        </div>
        <Button onClick={save} disabled={saving} className="bg-orange hover:bg-orange-dark text-white rounded-full shrink-0" data-testid="homepage-save">
          {saving ? <Loader2 size={15} className="mr-1.5 animate-spin" /> : <Save size={15} className="mr-1.5" />} Save changes
        </Button>
      </div>

      <Card title="Announcement bar (top of homepage)" defaultOpen>
        <label className="flex items-center gap-2 mb-4 cursor-pointer">
          <input type="checkbox" checked={!!form.announcement?.enabled} onChange={(e) => setAnn("enabled", e.target.checked)} data-testid="hp-announcement-enabled" className="w-4 h-4 accent-teal-600" />
          <span className="text-sm font-semibold text-navy">Show the announcement bar above the homepage hero</span>
        </label>
        <label className="flex items-center gap-2 mb-4 cursor-pointer">
          <input type="checkbox" checked={!!form.announcement?.ab_enabled} onChange={(e) => setAnn("ab_enabled", e.target.checked)} data-testid="hp-announcement-ab" className="w-4 h-4 accent-teal-600" />
          <span className="text-sm font-semibold text-navy">A/B test — randomly show one of two headlines and track which gets more clicks</span>
        </label>
        <div className="grid md:grid-cols-2 gap-4">
          <Field label="Headline — Variant A" value={form.announcement?.variants?.[0]?.text} onChange={(v) => setVariant(0, v)} placeholder="Looking for CASS Legal Cashiering?" />
          <Field label={`Headline — Variant B${form.announcement?.ab_enabled ? "" : " (unused while A/B is off)"}`} value={form.announcement?.variants?.[1]?.text} onChange={(v) => setVariant(1, v)} placeholder="Specialist legal cashiering for conveyancers" />
          <Field label="Link label (the clickable part)" value={form.announcement?.cta_label} onChange={(v) => setAnn("cta_label", v)} placeholder="Click here" />
          <Field label="Link destination" value={form.announcement?.link} onChange={(v) => setAnn("link", v)} placeholder="/legal-cashiering" />
        </div>
        <p className="text-xs text-slate-400 mt-2">When A/B is on, each visitor randomly sees Variant A or B (fixed for their visit). Visitors can dismiss the bar. Styled in the Legal Cashiering teal. Remember to Save changes after editing.</p>
        <AnnouncementStats />
      </Card>

      <Card title="Hero" defaultOpen>
        <div className="grid md:grid-cols-2 gap-4">
          <Field label="Eyebrow" value={form.hero.eyebrow} onChange={(v) => setHero("eyebrow", v)} />
          <Field label="Headline" value={form.hero.headline} onChange={(v) => setHero("headline", v)} />
          <Field label="Subheading" value={form.hero.subheading} onChange={(v) => setHero("subheading", v)} area />
          <Field label="Intro paragraph" value={form.hero.intro} onChange={(v) => setHero("intro", v)} area />
          <Field label="Primary button label" value={form.hero.primary_cta_label} onChange={(v) => setHero("primary_cta_label", v)} />
          <Field label="Primary button link" value={form.hero.primary_cta_link} onChange={(v) => setHero("primary_cta_link", v)} />
          <Field label="Secondary button label" value={form.hero.secondary_cta_label} onChange={(v) => setHero("secondary_cta_label", v)} />
          <Field label="Secondary button link" value={form.hero.secondary_cta_link} onChange={(v) => setHero("secondary_cta_link", v)} />
        </div>
        <div className="mt-4"><ImageUpload label="Hero image (team photo recommended)" value={form.hero.image} onChange={(v) => setHero("image", v)} /></div>
      </Card>

      <Card title="Outcomes (Section 2)">
        <div className="grid md:grid-cols-2 gap-4 mb-4">
          <Field label="Heading" value={form.outcomes_heading} onChange={(v) => set("outcomes_heading", v)} />
          <Field label="Intro" value={form.outcomes_intro} onChange={(v) => set("outcomes_intro", v)} area />
        </div>
        <ListEditor items={form.outcomes} onChange={(v) => set("outcomes", v)} template={{ icon: "Star", title: "", text: "" }}
          render={(it, upd) => (
            <div className="grid md:grid-cols-3 gap-3 pr-6">
              <IconSelect value={it.icon} onChange={(v) => upd({ icon: v })} />
              <Field label="Title" value={it.title} onChange={(v) => upd({ title: v })} />
              <Field label="Text" value={it.text} onChange={(v) => upd({ text: v })} />
            </div>
          )} />
      </Card>

      <Card title="Why CASS + Stat counters (Section 3)">
        <div className="grid md:grid-cols-2 gap-4 mb-4">
          <Field label="Heading" value={form.why_heading} onChange={(v) => set("why_heading", v)} />
          <Field label="Intro" value={form.why_intro} onChange={(v) => set("why_intro", v)} area />
        </div>
        <p className="text-xs font-semibold text-slate-500 mb-2">Differentiators</p>
        <ListEditor items={form.differentiators} onChange={(v) => set("differentiators", v)} template={{ icon: "CheckCircle2", label: "" }}
          render={(it, upd) => (
            <div className="grid md:grid-cols-2 gap-3 pr-6">
              <IconSelect value={it.icon} onChange={(v) => upd({ icon: v })} />
              <Field label="Label" value={it.label} onChange={(v) => upd({ label: v })} />
            </div>
          )} />
        <p className="text-xs font-semibold text-slate-500 mb-2 mt-4">Animated counters</p>
        <ListEditor items={form.stats} onChange={(v) => set("stats", v)} template={{ value: 0, suffix: "", label: "" }}
          render={(it, upd) => (
            <div className="grid md:grid-cols-3 gap-3 pr-6">
              <label className="block"><span className="text-xs font-semibold text-navy">Number</span><input type="number" value={it.value} onChange={(e) => upd({ value: Number(e.target.value) })} className={`${inputCls} mt-1`} /></label>
              <Field label="Suffix (e.g. + or %)" value={it.suffix} onChange={(v) => upd({ suffix: v })} />
              <Field label="Label" value={it.label} onChange={(v) => upd({ label: v })} />
            </div>
          )} />
      </Card>

      <Card title="Services (Section 4)">
        <div className="grid md:grid-cols-2 gap-4 mb-4">
          <Field label="Heading" value={form.services_heading} onChange={(v) => set("services_heading", v)} />
          <Field label="Intro" value={form.services_intro} onChange={(v) => set("services_intro", v)} area />
        </div>
        <ListEditor items={form.service_categories} onChange={(v) => set("service_categories", v)} template={{ icon: "Calculator", title: "", intro: "", slugs: [] }}
          render={(it, upd) => (
            <div className="grid md:grid-cols-2 gap-3 pr-6">
              <IconSelect value={it.icon} onChange={(v) => upd({ icon: v })} />
              <Field label="Category title" value={it.title} onChange={(v) => upd({ title: v })} />
              <Field label="Intro" value={it.intro} onChange={(v) => upd({ intro: v })} area />
              <Field label="Service slugs (comma-separated — use slug|Custom Label to rename or duplicate a link)" value={(it.slugs || []).join(", ")} onChange={(v) => upd({ slugs: v.split(",").map((s) => s.trim()).filter(Boolean) })} placeholder="cash-flow-forecasting|Cash Flow Forecasting, cash-flow-forecasting|Business Advisory" />
            </div>
          )} />
      </Card>

      <Card title="Three Pillars of Smart Finance">
        <div className="grid md:grid-cols-3 gap-4 mb-4">
          <Field label="Eyebrow" value={form.pillars_eyebrow} onChange={(v) => set("pillars_eyebrow", v)} />
          <Field label="Heading" value={form.pillars_heading} onChange={(v) => set("pillars_heading", v)} />
          <Field label="Intro" value={form.pillars_intro} onChange={(v) => set("pillars_intro", v)} area />
        </div>
        <ListEditor items={form.pillars} onChange={(v) => set("pillars", v)} template={{ number: "", icon: "Star", title: "", description: "" }}
          render={(it, upd) => (
            <div className="grid md:grid-cols-4 gap-3 pr-6">
              <Field label="Number (e.g. 01)" value={it.number} onChange={(v) => upd({ number: v })} />
              <IconSelect value={it.icon} onChange={(v) => upd({ icon: v })} />
              <Field label="Title" value={it.title} onChange={(v) => upd({ title: v })} />
              <Field label="Description" value={it.description} onChange={(v) => upd({ description: v })} area />
            </div>
          )} />
      </Card>

      <Card title="Meet the Founder">
        <div className="grid md:grid-cols-2 gap-4">
          <Field label="Eyebrow" value={form.founder?.eyebrow} onChange={(v) => setFounder("eyebrow", v)} />
          <Field label="Name" value={form.founder?.name} onChange={(v) => setFounder("name", v)} />
          <Field label="Role / title" value={form.founder?.role} onChange={(v) => setFounder("role", v)} />
          <div />
          <div className="md:col-span-2"><Field label="Bio (leave a blank line between paragraphs)" value={form.founder?.bio} onChange={(v) => setFounder("bio", v)} area /></div>
          <Field label="CTA button label" value={form.founder?.cta_label} onChange={(v) => setFounder("cta_label", v)} />
          <Field label="CTA button link" value={form.founder?.cta_link} onChange={(v) => setFounder("cta_link", v)} />
          <div className="md:col-span-2"><Field label="Founder LinkedIn URL (optional — strengthens SEO/AEO entity signal)" value={form.founder?.linkedin} onChange={(v) => setFounder("linkedin", v)} placeholder="https://www.linkedin.com/in/…" /></div>
        </div>
        <div className="mt-4">
          <p className="text-xs font-semibold text-slate-500 mb-2">Highlights (short credibility points)</p>
          <div className="space-y-2">
            {(form.founder?.highlights || []).map((h, i) => (
              <div key={i} className="flex items-center gap-2">
                <input value={h} onChange={(e) => setFounder("highlights", (form.founder.highlights || []).map((x, xi) => (xi === i ? e.target.value : x)))} className={inputCls} data-testid={`founder-highlight-input-${i}`} />
                <button onClick={() => setFounder("highlights", (form.founder.highlights || []).filter((_, xi) => xi !== i))} className="text-red-400 hover:text-red-600 shrink-0"><Trash2 size={15} /></button>
              </div>
            ))}
            <Button variant="outline" size="sm" onClick={() => setFounder("highlights", [...(form.founder?.highlights || []), ""])} className="rounded-full"><Plus size={14} className="mr-1" /> Add highlight</Button>
          </div>
        </div>
        <div className="mt-4"><ImageUpload label="Founder photo (used if no video is added)" value={form.founder?.image} onChange={(v) => setFounder("image", v)} /></div>
        <div className="mt-4"><Field label="Founder video — YouTube / Vimeo link (optional)" value={form.founder?.video_url} onChange={(v) => setFounder("video_url", v)} placeholder="https://www.youtube.com/watch?v=..." /></div>
        <p className="text-xs text-slate-400 mt-2 mb-1">…or upload a video file (takes priority over the link). The founder photo is used as the video thumbnail if set.</p>
        <div className="mt-2"><VideoUpload label="" value={form.founder?.video_upload} onChange={(v) => setFounder("video_upload", v)} /></div>
      </Card>

      <Card title="Meet the team (Section 5)">
        <div className="grid md:grid-cols-2 gap-4">
          <Field label="Heading" value={form.team_heading} onChange={(v) => set("team_heading", v)} />
          <Field label="Intro" value={form.team_intro} onChange={(v) => set("team_intro", v)} area />
          <Field label="Button label" value={form.team_cta_label} onChange={(v) => set("team_cta_label", v)} />
          <Field label="Button link" value={form.team_cta_link} onChange={(v) => set("team_cta_link", v)} />
        </div>
        <div className="mt-4"><ImageUpload label="Team photo" value={form.team_image} onChange={(v) => set("team_image", v)} /></div>
      </Card>

      <Card title="Technology (Section 7)">
        <div className="grid md:grid-cols-2 gap-4 mb-4">
          <Field label="Heading" value={form.tech_heading} onChange={(v) => set("tech_heading", v)} />
          <Field label="Intro" value={form.tech_intro} onChange={(v) => set("tech_intro", v)} area />
        </div>
        <ListEditor items={form.tech_partners} onChange={(v) => set("tech_partners", v)} template={{ name: "", logo: "", url: "" }}
          render={(it, upd) => (
            <div className="pr-6 space-y-3">
              <Field label="Partner name (shown if no logo; used as alt text)" value={it.name} onChange={(v) => upd({ name: v })} />
              <ImageUpload label="Logo image (displayed instead of the name)" value={it.logo} onChange={(v) => upd({ logo: v })} />
              <Field label="Website URL (optional) — clicking the logo opens this in a new tab" value={it.url} onChange={(v) => upd({ url: v })} placeholder="https://www.xero.com" />
            </div>
          )} />
      </Card>

      <Card title="Client logos — “Trusted by” strip (Section 6)">
        <Field label="Strip label" value={form.clients_strip_label} onChange={(v) => set("clients_strip_label", v)} placeholder="Trusted by growing businesses" />
        <p className="text-xs text-slate-400 mt-1 mb-3">Upload client logos (ideally on transparent/white backgrounds). They appear as a subtle greyscale strip that colours on hover. Leave empty to hide the strip.</p>
        <ListEditor items={form.client_logos} onChange={(v) => set("client_logos", v)} template={{ name: "", logo: "" }}
          render={(it, upd) => (
            <div className="pr-6 space-y-3">
              <Field label="Client name (used as alt text)" value={it.name} onChange={(v) => upd({ name: v })} />
              <ImageUpload label="Logo image" value={it.logo} onChange={(v) => upd({ logo: v })} />
            </div>
          )} />
      </Card>

      <Card title="Insights & Final CTA (Sections 8–9)">
        <div className="grid md:grid-cols-2 gap-4">
          <Field label="Insights heading" value={form.insights_heading} onChange={(v) => set("insights_heading", v)} />
          <Field label="Insights intro" value={form.insights_intro} onChange={(v) => set("insights_intro", v)} area />
          <Field label="Insights button label" value={form.insights_cta_label} onChange={(v) => set("insights_cta_label", v)} />
          <label className="block">
            <span className="text-xs font-semibold text-navy">Featured free guide</span>
            <select value={form.featured_guide_id || ""} onChange={(e) => set("featured_guide_id", e.target.value)} className={`${inputCls} mt-1`} data-testid="hp-featured-guide">
              <option value="">— Auto (first available guide) —</option>
              {guides.map((g) => <option key={g.id} value={g.id}>{g.title}</option>)}
            </select>
            <span className="text-[11px] text-slate-400 mt-1 block">Choose which guide appears in the homepage "Free guide" card. Its picture is used automatically.</span>
          </label>
          <Field label="Final CTA heading" value={form.final_heading} onChange={(v) => set("final_heading", v)} />
          <Field label="Final CTA intro" value={form.final_intro} onChange={(v) => set("final_intro", v)} area />
          <Field label="Primary button label" value={form.final_primary_label} onChange={(v) => set("final_primary_label", v)} />
          <Field label="Primary button link" value={form.final_primary_link} onChange={(v) => set("final_primary_link", v)} />
          <Field label="Secondary button label" value={form.final_secondary_label} onChange={(v) => set("final_secondary_label", v)} />
          <Field label="Secondary button link" value={form.final_secondary_link} onChange={(v) => set("final_secondary_link", v)} />
        </div>
      </Card>
    </div>
  );
}
